import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, ExternalLink, Sparkles } from "lucide-react";
import { PLATFORMS } from "../lib/platforms";

const PROGRAMS = [
  {
    key: "amazon",
    title: "Amazon Associates",
    field: "amazon_tag",
    label: "Your Associate Tag (e.g. yourname-20)",
    apply: "https://affiliate-program.amazon.com/",
    note: "Sign up free. Need 3 qualified sales in 180 days to keep PA-API access. Tag is auto-appended to imported Amazon URLs.",
  },
  {
    key: "etsy",
    title: "Etsy Affiliate (via Awin)",
    field: "etsy_awin_id",
    label: "Awin Affiliate ID",
    apply: "https://www.awin.com/gb/affiliates/etsy",
    note: "Etsy runs their program through Awin. Imported Etsy URLs are wrapped via Awin's cread.php with your ID.",
  },
  {
    key: "shopify",
    title: "Shopify Affiliate Program",
    field: "shopify_ref",
    label: "Shopify Ref Code",
    apply: "https://www.shopify.com/affiliates",
    note: "For promoting Shopify itself. For individual Shopify stores, use each store's own affiliate program.",
  },
  {
    key: "shopcom",
    title: "Shop.com (MarketAmerica) UnFranchise",
    field: "shopcom_ref",
    label: "Shop.com Ref / Cashback ID",
    apply: "https://www.shop.com/info/affiliate",
    note: "MarketAmerica's affiliate model. Append your ref to URLs.",
  },
];

export default function AdminSettings() {
  const [cfg, setCfg] = useState({ amazon_tag: "", etsy_awin_id: "", shopify_ref: "", shopcom_ref: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/affiliate/config").then((r) => setCfg(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/affiliate/config", cfg);
      toast.success("Affiliate settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12">Loading…</div>;

  return (
    <div className="p-8 lg:p-12 max-w-4xl" data-testid="admin-settings">
      <p className="overline text-white/40">Setup</p>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Affiliate Programs.</h1>
      <p className="text-sm text-white/60 mt-3 max-w-xl leading-relaxed">
        Apply to each program below. Once approved, paste your tag/ID here — every product you import will be auto-wrapped with the right affiliate link.
      </p>

      <div className="mt-10 space-y-4">
        {PROGRAMS.map((p) => {
          const platform = PLATFORMS.find((x) => x.key === p.key);
          return (
            <div key={p.key} className="admin-card p-6" data-testid={`program-${p.key}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: platform?.color }} />
                    <h3 className="font-display font-black text-xl tracking-tighter">{p.title}</h3>
                  </div>
                  <p className="text-xs text-white/50 mt-2 leading-relaxed max-w-md">{p.note}</p>
                </div>
                <a
                  href={p.apply}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`apply-${p.key}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider font-semibold border border-white/20 hover:bg-white/5"
                >
                  Apply <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="mt-5">
                <label className="overline text-white/40 block mb-2">{p.label}</label>
                <input
                  data-testid={`input-${p.field}`}
                  value={cfg[p.field] || ""}
                  onChange={(e) => setCfg({ ...cfg, [p.field]: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#E53935]"
                  placeholder={cfg[p.field] ? undefined : "Paste once you're approved"}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        data-testid="save-affiliate-config"
        onClick={save}
        disabled={saving}
        className="mt-8 inline-flex items-center gap-2 bg-[#E53935] text-white px-6 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C62828] transition-colors disabled:opacity-50"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
      </button>

      <div className="mt-12 border border-dashed border-white/15 p-6">
        <Sparkles className="w-5 h-5 text-[#E53935]" />
        <h3 className="font-display font-black text-2xl tracking-tighter mt-3">Don&apos;t have accounts yet?</h3>
        <p className="text-sm text-white/60 mt-2 leading-relaxed max-w-xl">
          No problem. You can still import any product URL via the <strong>Quick Import</strong> on the Products page — we&apos;ll scrape title, image, price, and description. Once you get approved later, we&apos;ll automatically wrap new imports with your tag.
        </p>
      </div>
    </div>
  );
}
